import { Fragment, memo, useContext, useMemo } from 'react';
import clsx from 'clsx';

import DraggableCourseBox from './DraggableCourseBox';
import StaticCourseBox from './StaticCourseBox';
import TbaContainer from './TbaContainer';
import ReactTimetableContext from '../context/ReactTimetableContext';
import { buildMeetingLanes } from '../utils/timetableActions';

import styles from './Timetable.module.css';

const WEEKDAYS = [
    ['Mo', 'MON'], ['Tu', 'TUE'], ['We', 'WED'], ['Th', 'THU'],
    ['Fr', 'FRI'], ['Sa', 'SAT'], ['Su', 'SUN'],
];

function formatTime(minutes) {
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function getSlots(startMinutes, endMinutes) {
    const slots = [];
    for (let minute = startMinutes; minute < endMinutes; minute += 30) {
        slots.push(minute);
    }
    return slots;
}

const TimetableRow = memo(({ lane, slots, weekday, readOnly }) => {
    const CourseBox = readOnly ? StaticCourseBox : DraggableCourseBox;
    const boxesByStart = useMemo(
        () => new Map(lane.map(box => [box.gridStartMinutes, box])),
        [lane]
    );
    const cells = [];

    for (let index = 0; index < slots.length;) {
        const slot = slots[index];
        const course = boxesByStart.get(slot);
        if (course) {
            cells.push(
                <td key={`${weekday}-${slot}`} colSpan={course.spanCount} className={styles.occupiedCell}>
                    <CourseBox
                        {...course}
                        timeStart={course.start}
                        timeEnd={course.end}
                        readOnly={readOnly}
                    />
                </td>
            );
            index += course.spanCount;
        } else {
            cells.push(<td key={`${weekday}-${slot}`} className={styles.emptyCell} />);
            index++;
        }
    }

    return <>{cells}</>;
});

const Timetable = memo(({
    courseBoxes: suppliedCourseBoxes,
    tbaBoxes: suppliedTbaBoxes,
    virtualBoxes: suppliedVirtualBoxes,
    readOnly = false,
}) => {
    const context = useContext(ReactTimetableContext);
    const courseBoxes = suppliedCourseBoxes || context.courseBoxes;
    const tbaBoxes = suppliedTbaBoxes || context.tbaBoxes;
    const virtualBoxes = suppliedVirtualBoxes || context.virtualBoxes;
    const allRegularBoxes = useMemo(
        () => [...courseBoxes, ...virtualBoxes.filter(box => box.type === 'regular')],
        [courseBoxes, virtualBoxes]
    );
    const gridBounds = context.gridBounds && !suppliedCourseBoxes
        ? context.gridBounds
        : (() => {
            const startMinutes = Math.min(9 * 60, ...allRegularBoxes.map(box => box.gridStartMinutes));
            const endMinutes = Math.max(23 * 60, ...allRegularBoxes.map(box => box.gridEndMinutes));
            return { startMinutes, endMinutes };
        })();
    const slots = useMemo(
        () => getSlots(gridBounds.startMinutes, gridBounds.endMinutes),
        [gridBounds.endMinutes, gridBounds.startMinutes]
    );

    return (
        <div className={styles.timetableContainer}>
            <table className={clsx(styles.timetable, 'content')} aria-label={readOnly ? 'Shared course timetable' : 'Course timetable'}>
                <thead>
                    <tr className={styles.timesTr}>
                        <th className={styles.corner} scope="col"><span className={styles.srOnly}>Day</span></th>
                        {slots.map(slot => (
                            <th
                                key={slot}
                                scope="col"
                                aria-label={formatTime(slot)}
                                className={clsx(styles.time, slot % 60 === 30 && styles.alt)}
                            >
                                {slot % 60 === 0 ? formatTime(slot) : ''}
                            </th>
                        ))}
                    </tr>
                </thead>
                {WEEKDAYS.map(([weekday, label]) => {
                    const lanes = buildMeetingLanes(allRegularBoxes.filter(box => box.weekday === weekday));
                    const rows = lanes.length > 0 ? lanes : [[]];

                    return (
                        <tbody key={weekday} className={styles.days}>
                            {rows.map((lane, index) => (
                                <Fragment key={`${weekday}-${index}`}>
                                    <tr className={styles.courseRow}>
                                        {index === 0 && (
                                            <th className={styles.weekday} scope="row" rowSpan={rows.length}>{label}</th>
                                        )}
                                        <TimetableRow lane={lane} slots={slots} weekday={weekday} readOnly={readOnly} />
                                    </tr>
                                </Fragment>
                            ))}
                        </tbody>
                    );
                })}
                <tbody className={clsx(styles.days, styles.tbaRow)}>
                    <tr>
                        <th className={styles.weekday} scope="row">TBA</th>
                        <td colSpan={slots.length} className={styles.tbaContainer}>
                            <TbaContainer
                                tbaBoxes={tbaBoxes}
                                virtualBoxes={virtualBoxes}
                                readOnly={readOnly}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
});

export default Timetable;
